/**
 * Printable HTML maternity summary (keep in sync with
 * infrastructure/lambda/maternity/maternityClaimFormHtml.js).
 */
import i18n from '../i18n/config'
import type { Patient } from '../types/patient'
import type { MaternityVisit, Pregnancy } from '../types/maternity'
import { formatDate, formatDateTime } from './locale'

function escapeHtml(value: unknown): string {
  if (value === undefined || value === null) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function dashIfEmpty(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—'
  return escapeHtml(value)
}

function documentHtmlLang(): string {
  const raw = (i18n.language || 'en-IE').trim().replace(/_/g, '-')
  if (/^[a-z]{2}(-[a-zA-Z0-9]+)*$/i.test(raw)) return raw
  return 'en-IE'
}

/** Calendar dates using app locale (matches list/detail screens). */
function formatClaimDateOnly(value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, d] = s.split('-').map(Number)
    return formatDate(new Date(y, mo - 1, d), 'dd/MM/yyyy')
  }
  const dt = new Date(s)
  if (Number.isNaN(dt.getTime())) return s
  return formatDate(dt, 'dd/MM/yyyy')
}

function formatClaimDateTime(value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  const s = String(value).trim()
  const dt = new Date(s)
  if (Number.isNaN(dt.getTime())) return s
  return formatDateTime(dt)
}

/** Clock times stay as-is; full timestamps use locale date-time. */
function formatClaimBirthTime(value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  const s = String(value).trim()
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s) && !s.includes('T')) return s
  return formatClaimDateTime(s)
}

function formatAddress(patient: Patient | null): string {
  if (!patient?.address || typeof patient.address !== 'object') return ''
  const a = patient.address
  const parts = [a.street, a.city, a.state, a.postalCode, a.country].filter(Boolean)
  return parts.length ? escapeHtml(parts.join(', ')) : ''
}

function formatUrineRaw(u: MaternityVisit['urineTest']): string {
  if (!u || typeof u !== 'object') return ''
  const parts: string[] = []
  if (u.protein) parts.push(`Protein ${u.protein}`)
  if (u.glucose) parts.push(`Glucose ${u.glucose}`)
  if (u.ketones) parts.push(`Ketones ${u.ketones}`)
  if (u.nitrites === true) parts.push('Nitrites +ve')
  if (u.nitrites === false) parts.push('Nitrites neg')
  if (u.leukocytes === true) parts.push('Leukocytes +ve')
  if (u.leukocytes === false) parts.push('Leukocytes neg')
  return parts.length ? parts.join(', ') : ''
}

function formatInterventions(list: MaternityVisit['interventions']): string {
  if (!Array.isArray(list) || list.length === 0) return '—'
  return list
    .map((iv) => {
      const bits = [iv.type, iv.description].filter(Boolean).join(': ')
      const when = iv.performedAt ? ` (${formatClaimDateTime(iv.performedAt)})` : ''
      const by = iv.performedBy ? ` — ${iv.performedBy}` : ''
      const note = iv.notes ? ` — ${iv.notes}` : ''
      return escapeHtml(`${bits}${when}${by}${note}`)
    })
    .join('<br>')
}

function formatVisitClinicalRow(v: MaternityVisit): string {
  const parts: string[] = []
  if (v.consultationId) parts.push(`Consultation ${v.consultationId}`)
  if (v.bloodPressure?.systolic != null && v.bloodPressure?.diastolic != null) {
    parts.push(`BP ${v.bloodPressure.systolic}/${v.bloodPressure.diastolic}`)
  }
  if (v.weight != null) parts.push(`Wt ${v.weight} kg`)
  if (v.fundalHeight != null) parts.push(`FHt ${v.fundalHeight} cm`)
  if (v.fetalHeartRate != null) parts.push(`FHR ${v.fetalHeartRate} bpm`)
  if (v.presentation) parts.push(String(v.presentation))
  if (v.cervicalDilation != null) parts.push(`Cx ${v.cervicalDilation} cm`)
  const u = formatUrineRaw(v.urineTest)
  if (u) parts.push(u)
  if (v.maternalMood) parts.push(String(v.maternalMood))
  if (v.customFields && typeof v.customFields === 'object' && Object.keys(v.customFields).length > 0) {
    try {
      parts.push(`Other: ${JSON.stringify(v.customFields)}`)
    } catch {
      parts.push('Other: (see EHR)')
    }
  }
  if (!parts.length) return '—'
  return escapeHtml(parts.join(' · '))
}

function gmsFromPatient(patient: Patient | null): string {
  if (!patient) return ''
  const n = patient.gmsNumber
  if (n) return String(n)
  const g = patient.coverage?.gms?.number
  return g ? String(g) : ''
}

export interface MaternityClaimFormParams {
  patient: Patient | null
  pregnancy: Pregnancy
  visits: MaternityVisit[]
  generatedAt: string
  preparedBy?: string
}

export function buildMaternityClaimFormHtml(params: MaternityClaimFormParams): string {
  const { patient, pregnancy, visits, generatedAt, preparedBy } = params
  const docLang = escapeHtml(documentHtmlLang())
  const patientName = patient
    ? [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim()
    : ''
  const patientLine = patientName || '—'
  const addr = formatAddress(patient)
  const gms = gmsFromPatient(patient)

  const babyRows =
    Array.isArray(pregnancy.babies) && pregnancy.babies.length > 0
      ? pregnancy.babies
          .map((b, i) => {
            const apgar =
              b.apgarScore1 != null || b.apgarScore5 != null
                ? `${b.apgarScore1 ?? '—'} / ${b.apgarScore5 ?? '—'}`
                : '—'
            return `<tr>
              <td>${escapeHtml(String(i + 1))}</td>
              <td>${dashIfEmpty(b.name)}</td>
              <td>${dashIfEmpty(b.gender)}</td>
              <td>${b.birthWeight != null ? escapeHtml(String(b.birthWeight)) : '—'}</td>
              <td>${b.birthLength != null ? escapeHtml(String(b.birthLength)) : '—'}</td>
              <td>${escapeHtml(apgar)}</td>
              <td>${dashIfEmpty(formatClaimBirthTime(b.birthTime))}</td>
              <td>${dashIfEmpty(b.babyId)}</td>
            </tr>`
          })
          .join('')
      : ''

  const visitRows =
    visits.length > 0
      ? visits
          .map((v, i) => {
            const detail = formatVisitClinicalRow(v)
            const iv = formatInterventions(v.interventions)
            const notes = v.notes ? escapeHtml(v.notes) : '—'
            return `<tr>
              <td class="num">${i + 1}</td>
              <td>${dashIfEmpty(formatClaimDateOnly(v.visitDate))}</td>
              <td>${dashIfEmpty(v.visitType)}</td>
              <td>${dashIfEmpty(v.gestationalAge)}</td>
              <td class="wide">${detail}</td>
              <td class="wide small">${iv}</td>
              <td class="wide small">${notes}</td>
            </tr>`
          })
          .join('')
      : `<tr><td colspan="7" class="muted">No visits recorded.</td></tr>`

  const pregNotes = pregnancy.notes ? escapeHtml(pregnancy.notes) : '—'

  return `<!DOCTYPE html>
<html lang="${docLang}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Maternity summary — ${escapeHtml(pregnancy.pregnancyNumber != null ? `Pregnancy ${pregnancy.pregnancyNumber}` : pregnancy.pregnancyId)}</title>
  <style>
    :root {
      --ink: #142033;
      --muted: #5a6578;
      --line: #c9d0dc;
      --panel: #f0f3f8;
      --accent: #1e4d7a;
    }
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif;
      color: var(--ink);
      line-height: 1.45;
      margin: 0;
      padding: 0;
      background: #fff;
      font-size: 11.5pt;
    }
    .sheet {
      max-width: 210mm;
      margin: 0 auto;
      padding: 14mm 16mm;
    }
    @page {
      size: A4;
      margin: 12mm;
    }
    @media print {
      body { font-size: 10.5pt; }
      .sheet { padding: 0; max-width: none; }
      .no-print { display: none !important; }
      thead { display: table-header-group; }
      tr, .block, section { page-break-inside: avoid; }
      .visit-table tr:nth-child(even) { background: #fafbfc; }
    }
    header.doc-head {
      border-bottom: 3px solid var(--accent);
      padding-bottom: 12px;
      margin-bottom: 18px;
    }
    header.doc-head h1 {
      margin: 0 0 4px 0;
      font-size: 1.55rem;
      font-weight: 650;
      letter-spacing: -0.02em;
      color: var(--accent);
    }
    header.doc-head p.sub {
      margin: 0;
      color: var(--muted);
      font-size: 0.95rem;
    }
    .meta {
      display: flex;
      flex-wrap: wrap;
      gap: 12px 28px;
      font-size: 0.88rem;
      color: var(--muted);
      margin-bottom: 20px;
    }
    .meta strong { color: var(--ink); font-weight: 600; }
    section {
      margin-bottom: 18px;
      page-break-inside: avoid;
    }
    section h2 {
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.09em;
      color: var(--accent);
      margin: 0 0 8px 0;
      font-weight: 700;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px 24px;
      background: var(--panel);
      border: 1px solid var(--line);
      border-radius: 6px;
      padding: 12px 14px;
    }
    .grid.full { grid-template-columns: 1fr; }
    .field { font-size: 0.92rem; }
    .field .label {
      display: block;
      font-size: 0.68rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--muted);
      margin-bottom: 2px;
    }
    .field .value { font-weight: 500; word-break: break-word; }
    table.data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.86rem;
      border: 1px solid var(--line);
      border-radius: 6px;
      overflow: hidden;
    }
    table.data-table th,
    table.data-table td {
      border: 1px solid var(--line);
      padding: 8px 10px;
      text-align: left;
      vertical-align: top;
    }
    table.data-table th {
      background: var(--panel);
      font-size: 0.68rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--muted);
      font-weight: 700;
    }
    table.visit-table td.wide { min-width: 120px; }
    table.visit-table td.num { width: 28px; text-align: center; }
    .small { font-size: 0.82rem; }
    .muted { color: var(--muted); }
    .print-hint {
      margin-top: 22px;
      padding: 10px 12px;
      background: #fff9e6;
      border: 1px solid #e8d9a8;
      border-radius: 6px;
      font-size: 0.82rem;
      color: #5c4d26;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <header class="doc-head">
      <h1>Maternity summary / claim support</h1>
      <p class="sub">Clinical pregnancy and visit record for claims or correspondence (not a substitute for official scheme forms).</p>
    </header>

    <div class="meta">
      <span><strong>Generated</strong> ${dashIfEmpty(formatClaimDateTime(generatedAt))}</span>
      <span><strong>Prepared by</strong> ${preparedBy ? escapeHtml(preparedBy) : '—'}</span>
      <span><strong>Pregnancy record</strong> ${escapeHtml(pregnancy.pregnancyId)}</span>
    </div>

    <section>
      <h2>Patient</h2>
      <div class="grid">
        <div class="field"><span class="label">Name</span><span class="value">${escapeHtml(patientLine)}</span></div>
        <div class="field"><span class="label">Date of birth</span><span class="value">${patient?.dateOfBirth ? dashIfEmpty(formatClaimDateOnly(patient.dateOfBirth)) : '—'}</span></div>
        <div class="field"><span class="label">Patient number</span><span class="value">${patient?.patientNumber ? escapeHtml(String(patient.patientNumber)) : '—'}</span></div>
        <div class="field"><span class="label">Patient ID</span><span class="value">${escapeHtml(pregnancy.patientId)}</span></div>
        <div class="field"><span class="label">GMS number</span><span class="value">${gms ? escapeHtml(gms) : '—'}</span></div>
        <div class="field"><span class="label">PPS number</span><span class="value">${patient?.ppsNumber ? escapeHtml(String(patient.ppsNumber)) : '—'}</span></div>
        <div class="field"><span class="label">Phone</span><span class="value">${patient?.phone ? escapeHtml(String(patient.phone)) : '—'}</span></div>
        <div class="field" style="grid-column: 1 / -1;"><span class="label">Address</span><span class="value">${addr || '—'}</span></div>
      </div>
    </section>

    <section>
      <h2>Pregnancy</h2>
      <div class="grid">
        <div class="field"><span class="label">Pregnancy number</span><span class="value">${pregnancy.pregnancyNumber != null ? escapeHtml(String(pregnancy.pregnancyNumber)) : '—'}</span></div>
        <div class="field"><span class="label">Status</span><span class="value">${dashIfEmpty(pregnancy.status)}</span></div>
        <div class="field"><span class="label">Last menstrual period</span><span class="value">${dashIfEmpty(formatClaimDateOnly(pregnancy.lastMenstrualPeriod))}</span></div>
        <div class="field"><span class="label">Estimated due date</span><span class="value">${dashIfEmpty(formatClaimDateOnly(pregnancy.estimatedDueDate))}</span></div>
        <div class="field" style="grid-column: 1 / -1;"><span class="label">Pregnancy notes</span><span class="value">${pregNotes}</span></div>
      </div>
    </section>

    <section>
      <h2>Outcome &amp; delivery</h2>
      <div class="grid">
        <div class="field"><span class="label">Outcome</span><span class="value">${dashIfEmpty(pregnancy.outcome)}</span></div>
        <div class="field"><span class="label">Outcome date</span><span class="value">${dashIfEmpty(formatClaimDateOnly(pregnancy.outcomeDate))}</span></div>
        <div class="field"><span class="label">Gestational age at outcome</span><span class="value">${dashIfEmpty(pregnancy.gestationalAgeAtOutcome)}</span></div>
        <div class="field"><span class="label">Delivery method</span><span class="value">${dashIfEmpty(pregnancy.deliveryMethod)}</span></div>
        <div class="field"><span class="label">Delivery location</span><span class="value">${dashIfEmpty(pregnancy.deliveryLocation)}</span></div>
      </div>
    </section>

    ${
      babyRows
        ? `<section>
      <h2>Babies</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Gender</th>
            <th>Weight (kg)</th>
            <th>Length (cm)</th>
            <th>Apgar 1 / 5</th>
            <th>Birth time</th>
            <th>Baby patient ID</th>
          </tr>
        </thead>
        <tbody>${babyRows}</tbody>
      </table>
    </section>`
        : ''
    }

    <section>
      <h2>Maternity visits</h2>
      <table class="data-table visit-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Date</th>
            <th>Type</th>
            <th>GA</th>
            <th>Clinical</th>
            <th>Interventions</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          ${visitRows}
        </tbody>
      </table>
    </section>

    <p class="print-hint no-print">If the print dialog did not open, use <strong>Print</strong> (Ctrl/Cmd+P). You can close this tab when finished.</p>
  </div>
</body>
</html>`
}

/** Base64 for API response body (UTF-8 safe in browser). */
export function encodeUtf8Base64(html: string): string {
  const bytes = new TextEncoder().encode(html)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/** Inverse of encodeUtf8Base64; matches template download normalisation (data URL strip, whitespace). */
export function decodeUtf8Base64(content: string): string {
  if (!content || typeof content !== 'string') {
    throw new Error('Invalid content: content must be a non-empty string')
  }
  let clean = content.trim()
  if (clean.startsWith('data:')) {
    const base64Idx = clean.indexOf('base64,')
    if (base64Idx !== -1) {
      clean = clean.slice(base64Idx + 'base64,'.length).trim()
    }
  }
  clean = clean.replace(/\s/g, '')
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(clean)) {
    throw new Error('Invalid base64 content format')
  }
  const binary = atob(clean)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new TextDecoder('utf-8').decode(bytes)
}
